import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import axios from "axios";
import { assignFundSource } from "../assignFundSource";
import { useStageGuard } from "../useStageGuard";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import Loader from "../../../components/atoms/Loader";
import studentIcon from "../../../images/icons/student_icon.svg";
import styles from "../index.module.css";

const labelStyle: React.CSSProperties = {
  fontSize: "0.68rem",
  fontWeight: 600,
  color: "#999",
  letterSpacing: "0.7px",
  textTransform: "uppercase",
  marginBottom: "0.3rem",
  display: "block",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.72rem 0.9rem 0.72rem 2.4rem",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  fontSize: "0.88rem",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "Outfit, sans-serif",
  color: "#333",
  background: "#fff",
};

const iconWrap: React.CSSProperties = {
  position: "absolute",
  left: "0.75rem",
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
  pointerEvents: "none",
  color: "#c0c0c0",
};

function EligibilityCourseDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  const userData = useEligibilityData();

  const fromStage10 = location.state?.fromStage10 === true;

  useStageGuard(userData?.userId, userData?.applicationId, 3, fromStage10);

  const [instituteName, setInstituteName] = useState("");
  const [courseName, setCourseName] = useState("");
  const [courseFees, setCourseFees] = useState("");
  const [initialInstituteName, setInitialInstituteName] = useState("");
  const [initialCourseName, setInitialCourseName] = useState("");
  const [initialCourseFees, setInitialCourseFees] = useState("");

  const [selectedInstId, setSelectedInstId] = useState("");

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [courseResults, setCourseResults] = useState<any[]>([]);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const courseDropdownRef = useRef<HTMLDivElement>(null);
  const courseDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [tenure, setTenure] = useState("");
  const [initialTenure, setInitialTenure] = useState("");
  const [tenureOptions, setTenureOptions] = useState<any[]>([{ tenure: 6 }, { tenure: 9 }, { tenure: 12 }]);

  const [email, setEmail] = useState("");
  const [initialEmail, setInitialEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (userData?.userId && userData?.applicationId) {
      fetchSummary(userData.userId, userData.applicationId);
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(e.target as Node)) {
        setShowCourseDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchTenures = async (instituteId: string) => {
    try {
      const token = sessionStorage.getItem("eligibility_auth_token");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/products/instituteId?instituteId=${instituteId}`,
        { headers }
      );
      const result = await res.json();
      const products: any[] = result?.data?.details || result?.data || [];
      setTenureOptions(products.length > 0 ? products : [{ tenure: 6 }, { tenure: 9 }, { tenure: 12 }]);
      if (products.length === 1) setTenure(String(products[0].tenure));
    } catch {
      setTenureOptions([{ tenure: 6 }, { tenure: 9 }, { tenure: 12 }]);
    }
  };

  const fetchSummary = async (userId: string, applicationId: string) => {
    try {
      const token = sessionStorage.getItem("eligibility_auth_token");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const response = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userId}&applicationId=${applicationId}`,
        { headers }
      );
      const result = await response.json();
      const d = result?.data;
      if (d) {
        const inst = d.instituteName || "";
        const course = d.courseName || "";
        const fees = d.courseFees ? String(d.courseFees) : "";
        const ten = d.tenure ? String(d.tenure) : "";
        const em = d.email || "";
        setInstituteName(inst);
        setCourseName(course);
        setCourseFees(fees);
        setTenure(ten);
        setEmail(em);
        setInitialInstituteName(inst);
        setInitialCourseName(course);
        setInitialCourseFees(fees);
        setInitialTenure(ten);
        setInitialEmail(em);
        const rawInstId = d.instituteId;
        const instId = Array.isArray(rawInstId)
          ? rawInstId[0]?.id
          : rawInstId;
        if (instId) {
          setSelectedInstId(instId);
          fetchTenures(instId);
        }
      }
    } catch {
      // leave defaults
    }
  };

  const handleInstituteChange = (value: string) => {
    setInstituteName(value);
    setShowDropdown(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length >= 4) {
      debounceRef.current = setTimeout(async () => {
        try {
          const token = sessionStorage.getItem("eligibility_auth_token");
          const headers: Record<string, string> = {};
          if (token) headers["Authorization"] = `Bearer ${token}`;
          const res = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/institute/info/name?name=${encodeURIComponent(value)}`,
            { headers }
          );
          const result = await res.json();
          const list = result?.data || [];
          setSuggestions(list);
          setShowDropdown(list.length > 0);
        } catch {
          setSuggestions([]);
          setShowDropdown(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
    }
  };

  const handleSelectInstitute = (name: string, id: string) => {
    setInstituteName(name);
    setShowDropdown(false);
    setSuggestions([]);
    setSelectedInstId(id);
    setCourseName("");
    setCourseResults([]);
    setShowCourseDropdown(false);
    setTenure("");
    setTenureOptions([]);
    if (id) fetchTenures(id);
  };

  const handleCourseChange = (value: string) => {
    setCourseName(value);
    setShowCourseDropdown(false);
    if (courseDebounceRef.current) clearTimeout(courseDebounceRef.current);
    if (value.length >= 2 && selectedInstId) {
      courseDebounceRef.current = setTimeout(async () => {
        try {
          const token = sessionStorage.getItem("eligibility_auth_token");
          const headers: Record<string, string> = {};
          if (token) headers["Authorization"] = `Bearer ${token}`;
          const res = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/institute/course/search?instituteId=${selectedInstId}&name=${encodeURIComponent(value)}`,
            { headers }
          );
          const result = await res.json();
          const list = result?.data || [];
          setCourseResults(list);
          setShowCourseDropdown(list.length > 0);
        } catch {
          setCourseResults([]);
          setShowCourseDropdown(false);
        }
      }, 300);
    } else {
      setCourseResults([]);
    }
  };

  const selectCourse = (opt: any) => {
    setCourseName(opt.courseName);
    setShowCourseDropdown(false);
    setCourseResults([]);
  };

  const handleNext = async () => {
    setError("");

    const changed: Record<string, any> = {};
    if (instituteName !== initialInstituteName) changed.instituteName = instituteName;
    if (courseName !== initialCourseName) changed.courseName = courseName;
    if (courseFees !== initialCourseFees) changed.courseFees = courseFees;
    if (tenure !== initialTenure) changed.loanTenure = tenure ? Number(tenure) : undefined;
    if (email !== initialEmail) changed.email = email;

    const goNext = () => {
      if (!email.trim()) {
        navigate("/eligibility/email", { state: { data: userData } });
      } else {
        assignFundSource(userData, sessionStorage.getItem("eligibility_auth_token"));
        navigate("/eligibility/evaluating", { state: { data: userData } });
      }
    };

    if (Object.keys(changed).length === 0) {
      goNext();
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem("eligibility_auth_token");
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const payload = {
        applicationId: userData.applicationId,
        userId: userData.userId,
        ...changed,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_DASHBOARD_URL}/summary/update`,
        payload,
        { headers }
      );

      if (res.status === 200) {
        goNext();
      } else {
        setError("Oops! There is an issue at our end. Please contact us.");
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setError(msg || "Oops! There is an issue at our end. Please contact us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>
              <div className={styles.cardHeader}>
                <img src={studentIcon} alt="" />
                <p className={styles.cardTitle}>Course Details</p>
              </div>

              <div className={styles.content} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>

                {/* Institute Name */}
                <div>
                  <span style={labelStyle}>Institute Name</span>
                  <div ref={dropdownRef} style={{ position: "relative" }}>
                    <span style={iconWrap}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <path d="M12 3L2 9L12 15L22 9L12 3Z" stroke="#c0c0c0" strokeWidth="1.8" strokeLinejoin="round" />
                        <path d="M6 12V18C6 18 8.5 21 12 21C15.5 21 18 18 18 18V12" stroke="#c0c0c0" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    </span>
                    <input
                      style={inputStyle}
                      placeholder="e.g. Stanford University"
                      value={instituteName}
                      onChange={(e) => handleInstituteChange(e.target.value)}
                    />
                    {showDropdown && (
                      <div style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                        background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100,
                        maxHeight: "180px", overflowY: "auto",
                      }}>
                        {suggestions.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            onMouseDown={() => handleSelectInstitute(item.brandName, item.id)}
                            style={{
                              padding: "0.6rem 0.9rem", fontSize: "0.85rem", cursor: "pointer",
                              borderBottom: idx < suggestions.length - 1 ? "1px solid #f0f0f0" : "none",
                              color: "#222",
                            }}
                          >
                            {item.brandName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Course Name */}
                <div>
                  <span style={labelStyle}>Course Name</span>
                  <div ref={courseDropdownRef} style={{ position: "relative" }}>
                    <span style={iconWrap}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="5" y="3" width="14" height="18" rx="2" stroke="#c0c0c0" strokeWidth="1.8" />
                        <path d="M9 8H15M9 12H15M9 16H12" stroke="#c0c0c0" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </span>
                    <input
                      style={inputStyle}
                      placeholder="e.g. Master of Science in Finance"
                      value={courseName}
                      onChange={(e) => handleCourseChange(e.target.value)}
                    />
                    {showCourseDropdown && (
                      <div style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                        background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100,
                        maxHeight: "180px", overflowY: "auto",
                      }}>
                        {courseResults.map((opt: any, idx: number) => (
                          <div
                            key={opt.id ?? idx}
                            onMouseDown={() => selectCourse(opt)}
                            style={{
                              padding: "0.6rem 0.9rem", fontSize: "0.85rem", cursor: "pointer",
                              borderBottom: idx < courseResults.length - 1 ? "1px solid #f0f0f0" : "none",
                              color: "#222",
                            }}
                          >
                            {opt.courseName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Course Fee + Tenure */}
                <div style={{ display: "flex", gap: "0.6rem" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={labelStyle}>Course Fee</span>
                    <div style={{ position: "relative" }}>
                      <span style={{ ...iconWrap, fontSize: "0.9rem", fontWeight: 700, color: "#c0c0c0", fontFamily: "Outfit, sans-serif" }}>₹</span>
                      <input
                        style={inputStyle}
                        placeholder="0.00"
                        value={courseFees}
                        onChange={(e) => setCourseFees(e.target.value)}
                        type="number"
                      />
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={labelStyle}>Tenure</span>
                    <div style={{ position: "relative" }}>
                      <span style={iconWrap}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="5" width="18" height="16" rx="2" stroke="#c0c0c0" strokeWidth="1.8" />
                          <path d="M3 10H21" stroke="#c0c0c0" strokeWidth="1.6" />
                          <path d="M8 3V7M16 3V7" stroke="#c0c0c0" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </span>
                      <select
                        disabled={tenureOptions.length === 1}
                        style={{ ...inputStyle, appearance: "none", WebkitAppearance: "none", cursor: "pointer", color: tenure ? "#333" : "#aaa", paddingRight: "2rem" }}
                        value={tenure}
                        onChange={(e) => setTenure(e.target.value)}
                      >
                        <option value="" disabled>Select</option>
                        {tenureOptions.map((p: any) => (
                          <option key={p.tenure} value={String(p.tenure)}>{p.tenure} Months</option>
                        ))}
                      </select>
                      <span style={{ position: "absolute", right: "0.7rem", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M6 9L12 15L18 9" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              {error && (
                <p style={{ color: "#d32028", fontSize: "0.85rem", marginTop: "0.75rem", marginBottom: 0 }}>
                  {error}
                </p>
              )}
              {loading && <Loader />}
            </div>

            <div style={{ padding: "0.75rem 1.25rem 1rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.6rem" }}>
              <button
                onClick={handleNext}
                disabled={loading}
                style={{
                  width: "65%",
                  padding: "0.8rem 1.5rem",
                  background: loading ? "#aaa" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                {loading ? "Please wait..." : (
                  <>
                    Save & Continue
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/email", { state: { data: userData } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityCourseDetails;
